// Entity ids carry a type prefix (`CHAR#<uuid>`). The API accepts either the bare uuid
// or the URL-encoded prefixed form (`CHAR%23<uuid>`); the web consistently sends the
// bare uuid.
//
// These helpers exist because the codebase previously stripped prefixes four different
// ways — including `slice(5)` / `slice(4)` / `slice(6)`, which silently yield a wrong
// id if a prefix ever changes length — and built world-entity API paths by hand in
// three separate places.

/** `CHAR#abc` -> `abc`. Works for any prefix, and is a no-op on an already-bare id. */
export const bareId = (entityId: string): string => entityId.replace(/^[A-Z]+#/, '');

export type WorldEntityKind = 'character' | 'location' | 'event';

const COLLECTIONS: Record<WorldEntityKind, string> = {
  character: '/characters',
  location: '/locations',
  event: '/events',
};

/** Which world-entity collection an id belongs to. Event is the fallback. */
export function entityKind(entityId: string): WorldEntityKind {
  if (entityId.startsWith('CHAR#')) return 'character';
  if (entityId.startsWith('LOC#')) return 'location';
  return 'event';
}

/** Collection path for a kind, e.g. `character` -> `/characters`. */
export const collectionPath = (kind: WorldEntityKind): string => COLLECTIONS[kind];

/** API path for a single world entity, e.g. `CHAR#abc` -> `/characters/abc`. */
export const entityPath = (entityId: string): string =>
  `${COLLECTIONS[entityKind(entityId)]}/${encodeURIComponent(bareId(entityId))}`;
